import { isEmpty, noop } from 'lodash';
import { isMobile } from 'react-device-detect';
import { systemTypeKeyMap } from './widgets';

const chainOnChangeFields = [
  'requirementType',
  'type',
  'reportList',
  'report',
  'handler',
  'edition',
  'priority',
  ...(!isMobile ? ['expectCompleteTime'] : []),
];
const chainOnChangeWidgetTypes = [
  'select',
  'radio',
  'checkbox',
  'member',
  ...(!isMobile ? ['date'] : []),
];
export const shouldChainOnChange = ({ fieldType, widgetType }) => {
  return chainOnChangeFields.includes(fieldType) || chainOnChangeWidgetTypes.includes(widgetType);
};

const chainOnAfterChangeFields = [
  'name',
  'purpose',
  'description',
  'scene',
  'question',
  'goal',
  'tags',
  'note',
  'indexList',
  'progress',
  'dataset',
  'handleStartTime',
  'handleEndTime',
  ...(!isMobile ? [] : ['expectCompleteTime']),
];
const chainOnAfterChangeWidgetTypes = [
  'text',
  'textarea',
  'multiple_select',
  'number',
  ...(!isMobile ? [] : ['date']),
];
export const shouldChainOnAfterChange = ({ fieldType, widgetType }) => {
  return (
    chainOnAfterChangeFields.includes(fieldType) ||
    chainOnAfterChangeWidgetTypes.includes(widgetType)
  );
};

export const onFieldChain = (formData, customFieldMap = {}, chainGroups = [], onChain = noop) => {
  if (isEmpty(chainGroups)) {
    onChain([]);
    return;
  }

  const displayFields = [];
  const hiddenFields = [];
  chainGroups.forEach(({ conditions, actions }) => {
    if (isEmpty(actions)) {
      return;
    }

    const matchCond = conditions?.every((cond) => {
      return (
        textHandler(cond, formData) &&
        tagsHandler(cond, formData) &&
        progressHandler(cond, formData) &&
        idHandler(cond, formData) &&
        fineReportHandler(cond, formData) &&
        customSelectHandler(cond, formData, customFieldMap) &&
        customNumHandler(cond, formData, customFieldMap) &&
        customIdHandler(cond, formData, customFieldMap)
      );
    });
    if (!matchCond) {
      hiddenFields.push(...actions);
    } else {
      displayFields.push(...actions);
    }
  });
  if (isEmpty(hiddenFields)) {
    onChain([]);
    return;
  }

  onChain(hiddenFields.filter((item) => !displayFields.includes(item)));
};

const isCondEq = (op) => {
  return op === 'eq';
};

const isCondNeq = (op) => {
  return op === 'neq';
};

const isCondLike = (op) => {
  return op === 'like';
};

const isCondUnlike = (op) => {
  return op === 'unlike';
};

const isCondIn = (op) => {
  return op === 'in';
};

const isCondNotIn = (op) => {
  return op === 'nin';
};

const isCondEmpty = (op) => {
  return op === 'empty';
};

const isCondNotEmpty = (op) => {
  return op === 'not_empty';
};

const isCondLessThan = (op) => {
  return op === 'lt';
};

const isCondGreatThan = (op) => {
  return op === 'gt';
};

const isCondBetween = (op) => {
  return op === 'between';
};

const isCondBeyond = (op) => {
  return op === 'beyond';
};

const textHandler = (cond, formData) => {
  const { field, op, value } = cond;
  if (!['name', 'scene', 'question', 'goal'].includes(field)) {
    return true;
  }
};

const tagsHandler = (cond, formData) => {
  const { field, op, value } = cond;
  if (!['tags'].includes(field)) {
    return true;
  }

  const formValue = formData[field]?.map((tag) => tag.id) || [];
  const condValue = value
    ?.map((item) => item?.value)
    .map((item) => parseInt(item, 10))
    .filter((item) => item);

  return tagAndCustomSelectHandler(field, op, formValue, condValue);
};

const tagAndCustomSelectHandler = (field, op, formValue, condValue) => {
  if (isCondEmpty(op)) {
    return isEmpty(formValue);
  }
  if (isCondNotEmpty(op)) {
    return !isEmpty(formValue);
  }

  if (isCondIn(op)) {
    return formValue.some((item) => condValue.includes(item));
  }
  if (isCondNotIn(op)) {
    return formValue.every((item) => !condValue.includes(item));
  }
  // 要考虑用户不输入标签的情况
  // if (isEmpty(formValue)) {
  //     return false;
  // }

  const isEq =
    formValue.every((item) => condValue.includes(item)) &&
    condValue.every((item) => formValue.includes(item));
  if (isCondEq(op)) {
    return isEq;
  }
  if (isCondNeq(op)) {
    return !isEq;
  }
  return true;
};

const idHandler = (cond, formData) => {
  const { field, op, value } = cond;
  if (!['priority', 'edition', 'handler'].includes(field)) {
    return true;
  }

  let formValue;
  if (field === 'priority') {
    formValue = `${formData?.priority || ''}`;
  } else if (field === 'edition') {
    formValue = `${formData?.edition?.id || ''}`;
  } else {
    formValue = `${formData?.handler?.userId || ''}`;
  }

  const condValue = value
    ?.map((item) => item?.value)
    ?.map((item) => parseInt(item, 10))
    ?.filter((item) => item)
    ?.map((item) => `${item}`);
  return idOrCustomIdHandler(field, op, formValue, condValue);
};

const idOrCustomIdHandler = (field, op, formValue, condValue) => {
  if (isCondEmpty(op)) {
    return !formValue;
  }
  if (isCondNotEmpty(op)) {
    return !!formValue;
  }

  if (isCondIn(op)) {
    return condValue.includes(formValue);
  }
  if (isCondNotIn(op)) {
    return !condValue.includes(formValue);
  }
  return true;
};

const progressHandler = (cond, formData) => {
  const { field, op, value } = cond;
  if (!['progress'].includes(field)) {
    return true;
  }

  let formValue = '';
  if (field in formData) {
    formValue = `${formData[field]}`;
  }
  const condValue = value
    ?.map((item) => item?.value)
    ?.map((item) => parseFloat(item, 10) * 100)
    ?.filter((item) => item || item === 0)
    ?.map((item) => `${item}`);

  return progressOrCustomNumHandler(field, op, formValue, condValue, true);
};

const progressOrCustomNumHandler = (field, op, formValue, condValue, ignoreNeg = false) => {
  if (isCondEmpty(op) && ignoreNeg) {
    return !formValue || formValue === '-1';
  } else if (isCondEmpty(op)) {
    return !formValue;
  }
  if (isCondNotEmpty(op) && ignoreNeg) {
    return !!formValue && formValue !== '-1';
  } else if (isCondNotEmpty(op)) {
    return !!formValue;
  }

  let begin = condValue[0];
  let end = condValue[1];
  if (isCondEq(op)) {
    return formValue === begin;
  }
  if (isCondNeq(op)) {
    return formValue !== begin;
  }
  if (isEmpty(formValue)) {
    return false;
  }

  if (!isNaN(begin)) {
    begin = Number(begin) || undefined;
  }
  if (!isNaN(end)) {
    end = Number(end) || undefined;
  }
  formValue = Number(formValue) || undefined;

  if (isCondLessThan(op)) {
    return formValue < begin;
  }
  if (isCondGreatThan(op)) {
    return formValue > begin;
  }
  if (isCondBetween(op)) {
    return (!begin || formValue >= begin) && (!end || formValue <= end);
  }
  if (isCondBeyond(op)) {
    return formValue < begin || formValue > end;
  }
  return true;
};

const dateOrCustomDateHandler = (field, op, formValue, condValue) => {
  if (isCondEmpty(op)) {
    return !formValue;
  }
  if (isCondNotEmpty(op)) {
    return !!formValue;
  }

  let begin = condValue[0];
  let end = condValue[1];
  if (isCondEq(op)) {
    return formValue === begin;
  }
  if (isCondNeq(op)) {
    return formValue !== begin;
  }
  if (isEmpty(formValue)) {
    return false;
  }
  if (isCondLessThan(op)) {
    return formValue < begin;
  }
  if (isCondGreatThan(op)) {
    return formValue > begin;
  }
  if (isCondBetween(op)) {
    return (!begin || formValue >= begin) && (!end || formValue <= end);
  }
  if (isCondBeyond(op)) {
    return formValue < begin || formValue > end;
  }
  return true;
};

const fineReportHandler = (cond, formData) => {
  const { field, op, value } = cond;
  if (!['report', 'dataset'].includes(field)) {
    return true;
  }

  let formValue;
  if (field === 'report') {
    formValue = formData?.reportList?.map((item) => `${item.appId}:${item.id}`) || [];
  } else {
    formValue = formData?.dataset?.map((item) => `${item.appId}:${item.id}`) || [];
  }

  const condValue = value?.map((item) => item?.value)?.filter((item) => item);
  return tagAndCustomSelectHandler(field, op, formValue, condValue);
};

const customSelectHandler = (cond, formData, customFieldMap) => {
  const { field, op, value } = cond;
  if (!field.startsWith('custom_')) {
    return true;
  }
  const fieldId = field.replace('custom_', '');
  const customField = customFieldMap[fieldId];
  if (
    !customField ||
    !['multiple_select', 'select', 'checkbox', 'radio'].includes(customField.widgetType)
  ) {
    return true;
  }

  const formCustom = formData.customFields?.find(
    ({ customFieldId }) => `${customFieldId}` === fieldId,
  )?.value;
  const formValue = formCustom?.map(({ name }) => name) || [];

  const condValue = value?.map((item) => item?.value) || [];
  return tagAndCustomSelectHandler(field, op, formValue, condValue);
};

const customNumHandler = (cond, formData, customFieldMap) => {
  const { field, op, value } = cond;
  if (!field.startsWith('custom_')) {
    return true;
  }
  const fieldId = field.replace('custom_', '');
  const customField = customFieldMap[fieldId];
  if (!customField || !['number'].includes(customField.widgetType)) {
    return true;
  }

  const formCustom = formData.customFields?.find(
    ({ customFieldId }) => `${customFieldId}` === fieldId,
  )?.value;
  const formValue = `${formCustom?.[0]?.name || ''}`;

  const condValue = value?.map((item) => item?.value);
  return progressOrCustomNumHandler(field, op, formValue, condValue);
};

const customIdHandler = (cond, formData, customFieldMap) => {
  const { field, op, value } = cond;
  if (!field.startsWith('custom_')) {
    return true;
  }
  const fieldId = field.replace('custom_', '');
  const customField = customFieldMap[fieldId];
  if (!customField || !['member'].includes(customField.widgetType)) {
    return true;
  }

  const formCustom = formData.customFields?.find(
    ({ customFieldId }) => `${customFieldId}` === fieldId,
  )?.value;
  const formValue = `${formCustom?.[0]?.id || ''}`;

  const condValue = value
    ?.map((item) => item?.value)
    ?.map((item) => parseInt(item))
    ?.filter((item) => item)
    ?.map((item) => `${item}`);
  return idOrCustomIdHandler(field, op, formValue, condValue);
};

export const getViewFieldConfigKey = (typeId, nodeId) => {
  if (!typeId || !nodeId) {
    return '';
  }
  return 'customFields-' + typeId + '-' + nodeId;
};

export const formatViewFieldsConfig = (customShowFields) => {
  let configStr = '';
  customShowFields.forEach((it) => {
    if (it.fieldType === 'custom') {
      configStr = configStr.concat(`,custom-${it.customFieldId}`);
    } else {
      configStr = configStr.concat(`,${it.fieldType}`);
    }
  });

  configStr = configStr.concat(',');
  return configStr;
};

export const parseViewFieldsConfig = (value) => {
  if (isEmpty(value)) {
    return [];
  }

  return value
    .split(',')
    .filter((it) => it)
    .map((it, index) => {
      let fieldType = it;
      let customFieldId = '';
      if (it.startsWith('custom')) {
        fieldType = 'custom';
        customFieldId = it.replace('custom-', '');
      }

      return {
        fieldType: fieldType,
        sort: index,
        show: true,
        customFieldId: parseInt(customFieldId, 10) || undefined,
      };
    });
};

export const getFieldValue = (field) =>
  field.fieldType !== 'custom'
    ? systemTypeKeyMap[field.fieldType] || field.fieldType
    : `custom_${field.customField?.id}`;

export const itemTypeMap = {
  requirement_type: 0,
  workflow_node: 1,
};
